import React, { useState, useCallback, useEffect } from 'react';
import { SolicitarRelatorioResult, TIPO_CONSULTA_INFO, TipoConsultaRelatorio, SituacaoConsulta, SITUACAO_CONSULTA_INFO, ConsultarProtocoloResult } from '../types';
import { consultarProtocolo } from '../services/consultationService';
import { 
  ArrowLeft, 
  FileText, 
  Copy,
  ExternalLink,
  Clock,
  CheckCircle,
  Download,
  Crown,
  Car,
  Sparkles,
  RefreshCw,
  Hourglass,
  AlertCircle,
  FileDown
} from 'lucide-react';

interface RelatorioProtocoloResultProps {
  data: SolicitarRelatorioResult;
  onBack: () => void;
}

const RelatorioProtocoloResult: React.FC<RelatorioProtocoloResultProps> = ({ data, onBack }) => {
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [protocoloStatus, setProtocoloStatus] = useState<ConsultarProtocoloResult | null>(null);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);

  // Derived state
  const situacao: SituacaoConsulta = protocoloStatus?.situacao || 'em_processamento';
  const urlPdf = protocoloStatus?.urlPdf || data.linkResultado;
  const isFinished = situacao === 'finalizada';
  const isPartial = situacao === 'parcialmente_finalizada';

  const copyProtocolo = async () => {
    try {
      await navigator.clipboard.writeText(data.protocolo);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const checkStatus = useCallback(async () => {
    if (loading) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await consultarProtocolo(data.protocolo, 'PDF');
      setProtocoloStatus(response.result);
      
      // Stop auto-refresh if finished
      if (response.result.situacao === 'finalizada') {
        setAutoRefreshEnabled(false);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao verificar status';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [data.protocolo, loading]);

  // Auto-refresh every 30 seconds if not finished
  useEffect(() => {
    if (!autoRefreshEnabled || isFinished) return;

    const interval = setInterval(() => {
      checkStatus();
    }, 30000);

    return () => clearInterval(interval);
  }, [autoRefreshEnabled, isFinished, checkStatus]);

  // Check status on mount
  useEffect(() => {
    checkStatus();
  }, []);

  const tipoInfo = TIPO_CONSULTA_INFO[data.tipoConsulta as TipoConsultaRelatorio] || {
    name: data.tipoConsulta,
    description: 'Consulta de veículo'
  };

  const situacaoInfo = SITUACAO_CONSULTA_INFO[situacao];

  const getStatusIcon = () => {
    switch (situacao) {
      case 'finalizada':
        return <CheckCircle className="w-10 h-10 text-white" />;
      case 'parcialmente_finalizada':
        return <Hourglass className="w-10 h-10 text-white" />;
      default:
        return <Clock className="w-10 h-10 text-white animate-pulse" />;
    }
  };

  const getStatusBannerColors = () => {
    switch (situacao) {
      case 'finalizada':
        return 'from-emerald-600 to-teal-600';
      case 'parcialmente_finalizada':
        return 'from-blue-600 to-cyan-600';
      default:
        return 'from-amber-600 to-orange-600';
    }
  };

  const getStatusMessage = () => {
    switch (situacao) {
      case 'finalizada':
        return { title: 'Relatório Concluído!', subtitle: 'Seu relatório está pronto para download' };
      case 'parcialmente_finalizada':
        return { title: 'Parcialmente Disponível', subtitle: 'Uma versão parcial do relatório está disponível' };
      default:
        return { title: 'Relatório em Processamento', subtitle: 'Aguarde enquanto geramos seu relatório' };
    }
  };

  const statusMessage = getStatusMessage();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950/20 to-slate-950">
      {/* Background pattern */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMyMDI5M2EiIGZpbGwtb3BhY2l0eT0iMC40Ij48cGF0aCBkPSJNMzYgMzRoLTJ2LTRoMnY0em0wLTZ2LTRoLTJ2NGgyek0yNiAyNGgtMnY0aDJ2LTR6bTAgNmgtMnY0aDJ2LTR6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-30" />
      
      <div className="relative max-w-2xl mx-auto px-4 py-8 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <button 
            onClick={onBack}
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors group"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            <span>Voltar</span>
          </button>
          
          {/* Refresh Button */}
          <button
            onClick={checkStatus}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-slate-800/50 rounded-lg text-slate-300 hover:text-white hover:bg-slate-700/50 transition-all disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span className="text-sm">Atualizar Status</span>
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 shrink-0" />
            <p className="text-sm text-red-300">{error}</p>
          </div>
        )}

        {/* Main Card */}
        <div className="bg-gradient-to-br from-slate-900/95 to-slate-800/95 rounded-3xl border border-slate-700/50 overflow-hidden backdrop-blur-sm shadow-2xl">
          {/* Status Banner */}
          <div className={`bg-gradient-to-r ${getStatusBannerColors()} p-6`}>
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-2xl backdrop-blur-sm">
                {getStatusIcon()}
              </div>
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-white">{statusMessage.title}</h1>
                <p className="text-white/80 text-sm mt-1">
                  {statusMessage.subtitle}
                </p>
              </div>
              {/* Status Badge */}
              <div className={`px-3 py-1.5 rounded-full text-xs font-semibold ${
                isFinished 
                  ? 'bg-emerald-500/20 text-emerald-200' 
                  : isPartial 
                  ? 'bg-blue-500/20 text-blue-200'
                  : 'bg-amber-500/20 text-amber-200'
              }`}>
                {situacaoInfo.label}
              </div>
            </div>
          </div>

          {/* Protocol Info */}
          <div className="p-6 space-y-6">
            {/* Protocol Number */}
            <div className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700/50">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2 text-slate-400 text-sm">
                  <FileText className="w-4 h-4" />
                  <span>Número do Protocolo</span>
                </div>
                <button
                  onClick={copyProtocolo}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-all ${
                    copied 
                      ? 'bg-emerald-500/20 text-emerald-400'
                      : 'bg-slate-700/50 text-slate-400 hover:bg-slate-700 hover:text-white'
                  }`}
                >
                  {copied ? (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Copiado!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      Copiar
                    </>
                  )}
                </button>
              </div>
              <p className="text-3xl font-mono font-bold text-white tracking-wider break-all">
                {data.protocolo}
              </p>
            </div>

            {/* Vehicle & Type Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-slate-800/30 rounded-xl p-4">
                <div className="flex items-center gap-2 text-slate-400 text-xs mb-2">
                  <Car className="w-3.5 h-3.5" />
                  Placa Consultada
                </div>
                <p className="text-xl font-mono font-bold text-white">{data.placa}</p>
              </div>
              <div className="bg-slate-800/30 rounded-xl p-4">
                <div className="flex items-center gap-2 text-slate-400 text-xs mb-2">
                  <Crown className="w-3.5 h-3.5" />
                  Tipo de Relatório
                </div>
                <p className="text-lg font-bold text-white">{tipoInfo.name}</p>
              </div>
            </div>

            {/* Timestamp */}
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <Clock className="w-4 h-4" />
              <span>Solicitado em: {data.dataSolicitacao}</span>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3 pt-4">
              {/* PDF Download - Only show when available */}
              {(isFinished || isPartial) && urlPdf && (
                <a
                  href={urlPdf}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold py-4 px-6 rounded-2xl transition-all flex items-center justify-center gap-3 text-lg shadow-lg shadow-emerald-500/20"
                >
                  <FileDown className="w-5 h-5" />
                  {isFinished ? 'Baixar Relatório PDF' : 'Baixar Versão Parcial'}
                </a>
              )}
              
              {/* View Link */}
              <a
                href={data.linkResultado}
                target="_blank"
                rel="noopener noreferrer"
                className={`w-full ${
                  isFinished || isPartial
                    ? 'bg-slate-800/50 hover:bg-slate-700/50 text-slate-300'
                    : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white shadow-lg shadow-purple-500/20'
                } font-bold py-4 px-6 rounded-2xl transition-all flex items-center justify-center gap-3 text-lg`}
              >
                <ExternalLink className="w-5 h-5" />
                {isFinished || isPartial ? 'Ver no Site' : 'Acompanhar Relatório'}
              </a>
              
              {!isFinished && !isPartial && (
                <p className="text-center text-xs text-slate-500">
                  Status atualiza automaticamente a cada 30 segundos
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Progress Indicator - Show when processing */}
        {!isFinished && (
          <div className="bg-slate-900/80 rounded-2xl border border-slate-800/50 p-5">
            <div className="flex items-center gap-4 mb-4">
              <div className={`p-2 rounded-lg ${isPartial ? 'bg-blue-500/10' : 'bg-amber-500/10'}`}>
                {isPartial ? (
                  <Hourglass className="w-5 h-5 text-blue-400" />
                ) : (
                  <Clock className="w-5 h-5 text-amber-400 animate-pulse" />
                )}
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-white">
                  {isPartial ? 'Finalizando Relatório...' : 'Processando Dados...'}
                </h3>
                <p className="text-sm text-slate-400">
                  {isPartial 
                    ? 'Algumas informações ainda estão sendo coletadas'
                    : 'Consultando bases de dados e gerando relatório'
                  }
                </p>
              </div>
            </div>
            {/* Progress bar */}
            <div className="w-full bg-slate-800 rounded-full h-2 overflow-hidden">
              <div 
                className={`h-full rounded-full transition-all duration-1000 ${
                  isPartial 
                    ? 'bg-gradient-to-r from-blue-500 to-cyan-500 w-3/4'
                    : 'bg-gradient-to-r from-amber-500 to-orange-500 animate-pulse w-1/3'
                }`}
              />
            </div>
          </div>
        )}

        {/* Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-slate-900/80 rounded-2xl border border-slate-800/50 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-amber-500/10 rounded-lg">
                <Clock className="w-5 h-5 text-amber-400" />
              </div>
              <h3 className="font-semibold text-white">Tempo de Processamento</h3>
            </div>
            <p className="text-sm text-slate-400">
              O relatório geralmente fica pronto em alguns minutos. O status é atualizado automaticamente.
            </p>
          </div>

          <div className="bg-slate-900/80 rounded-2xl border border-slate-800/50 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-emerald-500/10 rounded-lg">
                <Download className="w-5 h-5 text-emerald-400" />
              </div>
              <h3 className="font-semibold text-white">Download do PDF</h3>
            </div>
            <p className="text-sm text-slate-400">
              Quando o relatório estiver pronto, o botão de download aparecerá automaticamente.
            </p>
          </div>
        </div>

        {/* Save Protocol Reminder */}
        <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-2xl border border-purple-500/20 p-5">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-purple-500/20 rounded-lg shrink-0">
              <Sparkles className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <h3 className="font-semibold text-white mb-1">Guarde seu Protocolo</h3>
              <p className="text-sm text-slate-400">
                Este relatório fica salvo no seu histórico. Você pode acessá-lo novamente a qualquer momento pelo painel principal.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-xs text-slate-500 pb-4">
          Consulta realizada via Pedigree do Carro • {data.apiSource}
        </div>
      </div>
    </div>
  );
};

export default RelatorioProtocoloResult;
